import type {
  Creature,
  CreatureStatblockRecallKnowledge,
} from '../api/client';

export type CreatureAdjustment = 'normal' | 'weak' | 'elite';

const DC_BY_LEVEL: Record<number, number> = {
  [-1]: 13,
  0: 14,
  1: 15,
  2: 16,
  3: 18,
  4: 19,
  5: 20,
  6: 22,
  7: 23,
  8: 24,
  9: 26,
  10: 27,
  11: 28,
  12: 30,
  13: 31,
  14: 32,
  15: 34,
  16: 35,
  17: 36,
  18: 38,
  19: 39,
  20: 40,
  21: 42,
  22: 44,
  23: 46,
  24: 48,
  25: 50,
};

export function isCreatureAdjustmentAvailable(
  level: number,
  adjustment: CreatureAdjustment,
  hp?: number,
): boolean {
  if (adjustment !== 'weak') return true;
  if (level < 1) return false;
  return hp === undefined || hp + creatureAdjustmentHpDelta(level, adjustment) >= 1;
}

export function adjustedCreatureLevel(
  level: number,
  adjustment: CreatureAdjustment,
): number {
  if (adjustment === 'elite') return level + (level === -1 || level === 0 ? 2 : 1);
  if (adjustment === 'weak') return level - (level === 1 ? 2 : 1);
  return level;
}

export function creatureAdjustmentHpDelta(
  level: number,
  adjustment: CreatureAdjustment,
): number {
  if (adjustment === 'normal') return 0;
  if (adjustment === 'elite') {
    if (level <= 1) return 10;
    if (level <= 4) return 15;
    if (level <= 19) return 20;
    return 30;
  }
  if (level <= 0) {
    throw new RangeError('Weak HP adjustment is not published for creatures of level 0 or lower');
  }
  if (level <= 2) return -10;
  if (level <= 5) return -15;
  if (level <= 20) return -20;
  return -30;
}

export function adjustedCreatureSummary(
  creature: Pick<Creature, 'level' | 'hp' | 'ac'>,
  adjustment: CreatureAdjustment,
) {
  const hp = creature.hp + creatureAdjustmentHpDelta(creature.level, adjustment);
  if (hp < 1) {
    throw new RangeError('The adjusted creature would have less than 1 HP');
  }
  const modifier = adjustment === 'elite' ? 2 : adjustment === 'weak' ? -2 : 0;
  return {
    level: adjustedCreatureLevel(creature.level, adjustment),
    hp,
    ac: creature.ac + modifier,
  };
}

function adjustLeadingModifier(value: string, delta: number): string {
  return value.replace(/^(\s*)([+-]?\d+)/, (_match, whitespace: string, number: string) => (
    `${whitespace}${Number(number) + delta}`
  ));
}

function adjustDamageFormula(formula: string, delta: number): string {
  const normalized = formula.trim();
  const dice = normalized.match(/^(\d+d\d+)([+-]\d+)?$/i);
  if (dice) {
    const constant = Number(dice[2] || 0) + delta;
    return `${dice[1]}${constant === 0 ? '' : constant > 0 ? `+${constant}` : constant}`;
  }
  if (/^-?\d+$/.test(normalized)) return String(Number(normalized) + delta);
  return formula;
}

function adjustRecall(
  recall: CreatureStatblockRecallKnowledge,
  originalLevel: number,
  effectiveLevel: number,
): CreatureStatblockRecallKnowledge {
  const originalDc = DC_BY_LEVEL[originalLevel];
  const effectiveDc = DC_BY_LEVEL[effectiveLevel];
  if (originalDc === undefined || effectiveDc === undefined) return recall;
  const delta = effectiveDc - originalDc;
  const adjustCheck = (check: CreatureStatblockRecallKnowledge['standard']) => ({
    ...check,
    dc: check.dc + delta,
    progression: check.progression.map((value) => value + delta),
  });
  return {
    ...recall,
    base_dc: recall.base_dc + delta,
    standard: adjustCheck(recall.standard),
    lore_easy: adjustCheck(recall.lore_easy),
    lore_very_easy: adjustCheck(recall.lore_very_easy),
  };
}

export function projectCreatureAdjustment(
  creature: Creature,
  adjustment: CreatureAdjustment,
): Creature {
  if (adjustment === 'normal') return creature;
  if (!isCreatureAdjustmentAvailable(creature.level, adjustment, creature.hp)) {
    throw new RangeError('The selected creature adjustment is not available at this level');
  }
  const summary = adjustedCreatureSummary(creature, adjustment);
  const modifier = adjustment === 'elite' ? 2 : -2;
  const projectedRecall = adjustRecall(
    creature.recall_knowledge,
    creature.level,
    summary.level,
  );
  if (!creature.statblock) {
    return {
      ...creature,
      ...summary,
      fortitude: adjustLeadingModifier(creature.fortitude, modifier),
      reflex: adjustLeadingModifier(creature.reflex, modifier),
      will: adjustLeadingModifier(creature.will, modifier),
      perception: creature.perception + modifier,
      recall_knowledge: projectedRecall,
    };
  }
  const data = creature.statblock.data;
  const projectedData = {
    ...data,
    recall_knowledge: adjustRecall(data.recall_knowledge, creature.level, summary.level),
    perception: {
      ...data.perception,
      modifier: data.perception.modifier === null
        ? null
        : data.perception.modifier + modifier,
    },
    skills: Object.fromEntries(
      Object.entries(data.skills).map(([name, value]) => [name, value + modifier]),
    ),
    defenses: {
      ...data.defenses,
      ac: {
        ...data.defenses.ac,
        value: data.defenses.ac.value === null ? null : data.defenses.ac.value + modifier,
      },
      hp: {
        ...data.defenses.hp,
        max: data.defenses.hp.max === null
          ? null
          : data.defenses.hp.max + creatureAdjustmentHpDelta(creature.level, adjustment),
        value: data.defenses.hp.value === null
          ? null
          : data.defenses.hp.value + creatureAdjustmentHpDelta(creature.level, adjustment),
      },
      saves: {
        fortitude: {
          ...data.defenses.saves.fortitude,
          value: data.defenses.saves.fortitude.value === null
            ? null
            : data.defenses.saves.fortitude.value + modifier,
        },
        reflex: {
          ...data.defenses.saves.reflex,
          value: data.defenses.saves.reflex.value === null
            ? null
            : data.defenses.saves.reflex.value + modifier,
        },
        will: {
          ...data.defenses.saves.will,
          value: data.defenses.saves.will.value === null
            ? null
            : data.defenses.saves.will.value + modifier,
        },
      },
    },
    strikes: data.strikes.map((strike) => ({
      ...strike,
      bonus: strike.bonus + modifier,
      damage: strike.damage.map((damage, index) => index === 0
        ? { ...damage, formula: adjustDamageFormula(damage.formula, modifier) }
        : damage),
    })),
    spellcasting: data.spellcasting.map((item) => item.type === 'spellcastingEntry'
      ? {
        ...item,
        dc: item.dc === null || item.dc === undefined ? item.dc : item.dc + modifier,
        attack: item.attack === null || item.attack === undefined
          ? item.attack
          : item.attack + modifier,
      }
      : item),
  };
  return {
    ...creature,
    ...summary,
    fortitude: adjustLeadingModifier(creature.fortitude, modifier),
    reflex: adjustLeadingModifier(creature.reflex, modifier),
    will: adjustLeadingModifier(creature.will, modifier),
    perception: creature.perception + modifier,
    recall_knowledge: projectedRecall,
    statblock: {
      ...creature.statblock,
      data: projectedData,
    },
  };
}
